import { isTruthy } from '@ardoq/common-helpers';
import * as profiling from '@ardoq/profiling';
import {
  routine,
  collectRoutines,
  extractPayload,
  ofType,
  dispatchAction,
} from '@ardoq/rxbeach';
import {
  notifyWorkspaceChanged,
  contextWorkspaceChanged,
  setPresentation,
  clearPresentation,
  setScenarioId,
  clearReference,
  setReference,
  clearComponent,
  setComponentId,
  setWorkspaceIds,
  closeAllWorkspaces,
  closeAllWorkspacesSilently,
  justCloseWorkspaces,
  resetState,
  registerWorkspaces,
  forceUpdateComponent,
  closeWorkspace,
  loadWorkspacesAndSetContext,
  closeOtherWorkspaces,
  switchToWorkspace,
  forceReloadWorkspaces,
  clearComponentId,
} from './ContextActions';
import { toggleExploreMode } from '../../presentation/actions';
import { closePresentation } from 'presentation/presentations2024/actions';
import { tap, withLatestFrom } from 'rxjs/operators';
import { context as Context } from 'context';
import { workspaceInterface } from '@ardoq/workspace-interface';
import { components$ } from 'streams/components/components$';
import { persistentWorkspaces$ } from 'streams/workspaces/workspaces$';
import { notifyPresentationCreatedByUser } from 'streams/presentations/actions';
import { websocket$ } from 'sync/websocket$';
import { isPresentationEvent } from 'streams/presentations/presentations$';
import { EventType } from 'sync/types';
import { filter } from 'rxjs';
import { componentInterface } from '@ardoq/component-interface';
import { contextInterface } from 'modelInterface/contextInterface';
import {
  references$,
  scenarioRelatedIds$,
} from 'streams/references/references$';

const handleWorkspaceChanged = routine(
  ofType(notifyWorkspaceChanged),
  extractPayload(),
  tap(({ workspaceId }) => {
    if (!workspaceId) return;
    const workspace = workspaceInterface.get(workspaceId);
    if (!workspace) return;
    dispatchAction(contextWorkspaceChanged({ workspace }));
  })
);

const handleSetPresentation = routine(
  ofType(setPresentation),
  extractPayload(),
  tap(presentation => Context.setPresentation(presentation))
);

const handleClearPresentation = routine(
  ofType(clearPresentation, closePresentation),
  tap(() => Context.setPresentation(null))
);

const handleSetScenarioId = routine(
  ofType(setScenarioId),
  extractPayload(),
  tap(scenarioId => Context.setScenarioId(scenarioId))
);

const handleToggleExploreMode = routine(
  ofType(toggleExploreMode),
  extractPayload(),
  tap(({ isExploreMode }) => Context.toggleExploreMode(isExploreMode))
);

const handleClearReference = routine(
  ofType(clearReference),
  tap(() => Context.unsetReference())
);

const handleSetReference = routine(
  ofType(setReference),
  extractPayload(),
  withLatestFrom(scenarioRelatedIds$, references$),
  filter(
    ([referenceId, scenarioRelatedIds]) => !scenarioRelatedIds.has(referenceId)
  ),
  tap(([referenceId, , references]) => {
    const referenceData = references.byId[referenceId];
    if (referenceData) {
      Context.setReference(referenceData);
    }
  })
);

const handleClearComponentId = routine(
  ofType(clearComponentId),
  tap(() => Context.unsetComponentId())
);

const handleClearComponent = routine(
  ofType(clearComponent),
  tap(() => Context.unsetComponent())
);

const handleSetComponentId = routine(
  ofType(setComponentId),
  extractPayload(),
  tap(componentId => Context.setComponentUnsafe(componentId))
);

const handleSetWorkspaceIds = routine(
  ofType(setWorkspaceIds),
  extractPayload(),
  tap(workspaceIds => {
    Context._workspaceIds = workspaceIds;
  })
);

const handleCloseAllWorkspaces = routine(
  ofType(closeAllWorkspaces),
  tap(() => Context.closeAllWorkspaces())
);

const handleCloseAllWorkspacesSilently = routine(
  ofType(closeAllWorkspacesSilently),
  tap(() => Context.closeAllWorkspacesSilently())
);

const handleJustCloseWorkspaces = routine(
  ofType(justCloseWorkspaces),
  tap(() =>
    Context.workspaceIds().forEach(workspaceId =>
      Context.justCloseWorkspace(workspaceId)
    )
  )
);

const handleResetState = routine(
  ofType(resetState),
  extractPayload(),
  tap(payload => Context.resetState(payload))
);

const handleContextComponentCleanup = components$.pipe(
  tap(comps => {
    const id = Context.componentId();
    if (id && !comps.byId[id]) {
      Context.unsetComponent();
    }
  })
);

const handleContextReferenceCleanup = references$.pipe(
  tap(refs => {
    const id = Context.referenceId();
    if (id && !refs.byId[id]) {
      Context.unsetReference();
    }
  })
);

const handlePresentationCreatedByUser = routine(
  ofType(notifyPresentationCreatedByUser),
  extractPayload(),
  tap(presentation => Context.setPresentation(presentation))
);

const handlePresentationWebSocketEvents = websocket$.pipe(
  filter(isPresentationEvent),
  tap(event => {
    const presentation = event.data;
    switch (event['event-type']) {
      case EventType.DELETE:
        if (Context.presentationId() === presentation._id) {
          Context.setPresentation(null);
        }
        break;
      case EventType.UPDATE:
        if (Context.presentationId() === presentation._id) {
          Context.setPresentation(presentation);
        }
        break;
      default:
        break;
    }
  })
);

const handleRegisterWorkspaces = routine(
  ofType(registerWorkspaces),
  extractPayload(),
  tap(({ workspaceIds }) =>
    Context.ensureWorkspaceSubscriptionInViewpointMode(workspaceIds)
  )
);

const forceUpdateComponentHandler = routine(
  ofType(forceUpdateComponent),
  extractPayload(),
  tap(({ cid, trackingLocation }) => {
    const componentId = componentInterface.get(cid)?._id;
    if (!componentId || !componentInterface.isComponent(componentId)) {
      return;
    }
    Context.unsetComponentId();
    contextInterface.setComponentById(componentId, trackingLocation);
  })
);

const handleCloseWorkspace = routine(
  ofType(closeWorkspace),
  extractPayload(),
  tap(({ workspaceId, options }) => {
    const foundWorkspaceId = Context.workspaceIds().find(
      id => id === workspaceId
    );
    if (!foundWorkspaceId) {
      return;
    }
    Context.closeWorkspace(workspaceId, options ?? {});
  })
);

const handleLoadWorkspacesAndSetContext = routine(
  ofType(loadWorkspacesAndSetContext),
  extractPayload(),
  withLatestFrom(persistentWorkspaces$),
  tap(([{ contextWorkspaceId, workspaceIds }, { byId }]) => {
    const contextWorkspace = byId[contextWorkspaceId];
    const workspaces = workspaceIds
      .map(workspaceId => byId[workspaceId])
      .filter(isTruthy);
    if (!contextWorkspace) {
      Context.loadWorkspacesInBackground(workspaces);
      return;
    }
    Context.loadWorkspacesAndSetContext(contextWorkspace, workspaces);
  })
);

const handleCloseOtherWorkspaces = routine(
  ofType(closeOtherWorkspaces),
  extractPayload(),
  tap(({ keepWorkspaceIds }) => {
    Context.workspaceIds()
      .filter(workspaceId => !keepWorkspaceIds.includes(workspaceId))
      .forEach(workspaceId => Context.closeWorkspace(workspaceId, {}));
  })
);

const handleSwitchToWorkspace = routine(
  ofType(switchToWorkspace),
  extractPayload(),
  tap(({ workspaceId }) => {
    Context.setContextWorkspace(workspaceId, true);
  })
);

const handleForceReloadWorkspacesRoutine = routine(
  ofType(forceReloadWorkspaces),
  extractPayload(),
  tap(async ({ workspaceIds }) => {
    const transaction = profiling.startTransaction(
      'reload workspaces',
      5000,
      profiling.Team.CORE
    );
    const workspaces = workspaceIds
      .map(id => workspaceInterface.get(id))
      .filter(isTruthy);
    await Context.forceReloadWorkspaces(workspaces, transaction);
    profiling.endTransaction(transaction);
  })
);

export const routines = collectRoutines(
  handleWorkspaceChanged,
  handleSetPresentation,
  handleClearPresentation,
  handleSetScenarioId,
  handleToggleExploreMode,
  handleClearReference,
  handleSetReference,
  handleClearComponentId,
  handleClearComponent,
  handleSetComponentId,
  handleSetWorkspaceIds,
  handleCloseAllWorkspaces,
  handleCloseAllWorkspacesSilently,
  handleJustCloseWorkspaces,
  handleResetState,
  handleContextComponentCleanup,
  handleContextReferenceCleanup,
  handlePresentationCreatedByUser,
  handlePresentationWebSocketEvents,
  handleRegisterWorkspaces,
  forceUpdateComponentHandler,
  handleCloseWorkspace,
  handleLoadWorkspacesAndSetContext,
  handleCloseOtherWorkspaces,
  handleSwitchToWorkspace,
  handleForceReloadWorkspacesRoutine
);
